import type { ReactNode } from "react";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "../../components/button";
import { Card } from "../../components/card";
import { CloseIcon } from "../../components/icons";
import { IconButton } from "../../components/icon-button";
import {
  FeedbackForm,
  FeedbackTextField,
  FeedbackPhoneField,
  FeedbackEmailField,
  FeedbackNumberField,
  FeedbackRatingField,
  FeedbackToggleField,
  FeedbackSelectField,
  FeedbackCheckboxGroup,
  FeedbackMultiValueField,
  FeedbackMultiSelectTagsField,
  FeedbackDropdownMultiSelectField,
} from "./feedback-form";
import { useFeedbackForm } from "./feedback-form-context";

export type FeedbackFieldDef = {
  name: string;
  type:
    | "text"
    | "textarea"
    | "phone"
    | "email"
    | "number"
    | "rating"
    | "toggle"
    | "select"
    | "checkbox-group"
    | "multi-value"
    | "multi-select-tags"
    | "dropdown-multi-select"
    | "label"
    | "list"
    | "separator";
  label: string;
  helpText?: string;
  required?: boolean;
  placeholder?: string;
  min?: number;
  options?: Array<{ value: string; label: string }>;
  max?: number;
  step?: number;
  headingLevel?: 1 | 2 | 3 | 4 | 5;
  listStyle?: "ul" | "ol";
  items?: string[];
  separatorStyle?: "hr" | "space";
  unobtrusive?: boolean;
  unobtrusivePrompt?: string;
  children?: FeedbackFieldDef[];
  condition?: { field: string; operator: "=" | "!="; value: string | number | boolean };
  visibleWhen?: { field: string; operator: "=" | "!="; value: string | number | boolean };
};

type FeedbackFormRendererProps = {
  metaTitle?: string;
  open: boolean;
  title: string;
  onClose: () => void;
  onSubmit: (values: Record<string, unknown>) => void;
  fields: FeedbackFieldDef[];
  submitLabel?: string;
  error?: string;
  unobtrusive?: boolean;
  unobtrusivePrompt?: string;
};

export function FeedbackFormRenderer({
  open,
  title,
  onClose,
  onSubmit,
  fields,
  metaTitle,
  submitLabel,
  error,
  unobtrusive,
  unobtrusivePrompt,
}: FeedbackFormRendererProps) {
  const [teaserOpen, setTeaserOpen] = useState(false);
  const [teaserDismissed, setTeaserDismissed] = useState(false);
  const teaserText = unobtrusivePrompt?.trim() || "Share your thoughts with us.";
  const modalOpen = unobtrusive ? teaserOpen : open;
  const handleClose = () => {
    if (unobtrusive) setTeaserOpen(false);
    onClose();
  };
  const handleDismissTeaser = () => {
    setTeaserDismissed(true);
    onClose();
  };

  return (
    <>
      {unobtrusive && !teaserDismissed ? (
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.98 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
        >
          <Card className="cf-feedback-unobtrusive-card cf-feedback-unobtrusive-floating">
            <div className="cf-feedback-unobtrusive-shell">
              <IconButton
                className="cf-feedback-unobtrusive-close"
                icon={<CloseIcon />}
                label="Dismiss"
                onClick={handleDismissTeaser}
              />
              <div className="cf-feedback-unobtrusive-body">
                <span className="cf-feedback-unobtrusive-title">{title}</span>
                <p className="cf-help" style={{ margin: 0 }}>
                  {teaserText}
                </p>
                <Button
                  type="button"
                  variant="solid"
                  tone="brand"
                  size="sm"
                  onClick={() => setTeaserOpen(true)}
                >
                  Share feedback
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>
      ) : null}
      {metaTitle ? <title>{metaTitle}</title> : null}
      <FeedbackForm
        open={modalOpen}
        title={title}
        onClose={handleClose}
        onSubmit={onSubmit}
        submitLabel={submitLabel}
        error={error}
      >
        <FeedbackFieldTree fields={fields} />
      </FeedbackForm>
    </>
  );
}

type FeedbackFieldGraph = {
  fieldLookup: Map<string, FeedbackFieldDef>;
  parentLookup: Map<string, string>;
};

function buildFieldGraph(fields: FeedbackFieldDef[]) {
  const fieldLookup = new Map<string, FeedbackFieldDef>();
  const parentLookup = new Map<string, string>();

  const walk = (items: FeedbackFieldDef[], parentName?: string) => {
    for (const item of items) {
      fieldLookup.set(item.name, item);
      if (parentName) parentLookup.set(item.name, parentName);
      if (item.children?.length) walk(item.children, item.name);
    }
  };

  walk(fields);
  return { fieldLookup, parentLookup };
}

function isFieldVisible(
  field: FeedbackFieldDef,
  values: Record<string, unknown>,
  graph: FeedbackFieldGraph,
  cache: Map<string, boolean>,
  visiting: Set<string>
) {
  const cached = cache.get(field.name);
  if (cached !== undefined) return cached;
  if (visiting.has(field.name)) return false;
  visiting.add(field.name);

  const parentName = graph.parentLookup.get(field.name);
  if (parentName) {
    const parentField = graph.fieldLookup.get(parentName);
    if (!parentField) {
      cache.set(field.name, false);
      visiting.delete(field.name);
      return false;
    }
    if (!isFieldVisible(parentField, values, graph, cache, visiting)) {
      cache.set(field.name, false);
      visiting.delete(field.name);
      return false;
    }
    if (parentField.type === "toggle" && values[parentName] !== true) {
      cache.set(field.name, false);
      visiting.delete(field.name);
      return false;
    }
  }

  const condition = field.condition ?? field.visibleWhen;
  if (condition) {
    const sourceField = graph.fieldLookup.get(condition.field);
    if (!sourceField || !isFieldVisible(sourceField, values, graph, cache, visiting)) {
      cache.set(field.name, false);
      visiting.delete(field.name);
      return false;
    }
    const visible = matchesCondition(condition, values);
    cache.set(field.name, visible);
    visiting.delete(field.name);
    return visible;
  }

  cache.set(field.name, true);
  visiting.delete(field.name);
  return true;
}

function FeedbackFieldTree({ fields }: { fields: FeedbackFieldDef[] }) {
  const { values } = useFeedbackForm();
  const graph = buildFieldGraph(fields);
  const cache = new Map<string, boolean>();
  const visiting = new Set<string>();

  return (
    <div data-testid="feedback-form-field-tree" className="cf-feedback-field-tree">
      {fields.map((field) => (
        <FeedbackFieldNode
          key={field.name}
          field={field}
          values={values}
          graph={graph}
          cache={cache}
          visiting={visiting}
        />
      ))}
    </div>
  );
}

function FeedbackFieldNode(props: {
  field: FeedbackFieldDef;
  values: Record<string, unknown>;
  graph: FeedbackFieldGraph;
  cache: Map<string, boolean>;
  visiting: Set<string>;
}) {
  const { field, values, graph, cache, visiting } = props;
  if (!isFieldVisible(field, values, graph, cache, visiting)) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
    >
      {renderField(field, values, graph, cache, visiting)}
    </motion.div>
  );
}

function matchesCondition(
  condition: NonNullable<FeedbackFieldDef["condition"]>,
  values: Record<string, unknown>
) {
  const current = values[condition.field];
  const expected = condition.value;

  if (typeof expected === "boolean") {
    const actual =
      typeof current === "boolean"
        ? current
        : typeof current === "string"
          ? current === "true"
            ? true
            : current === "false"
              ? false
              : null
          : null;
    if (actual === null) return false;
    return condition.operator === "=" ? actual === expected : actual !== expected;
  }

  if (typeof expected === "number") {
    if (current === "" || current == null) return false;
    const actual = typeof current === "number" ? current : Number(current);
    if (!Number.isFinite(actual)) return false;
    return condition.operator === "=" ? actual === expected : actual !== expected;
  }

  const actual = current == null ? "" : String(current);
  if (!actual) return false;
  const target = String(expected);
  return condition.operator === "=" ? actual === target : actual !== target;
}

function renderHeading(level: 1 | 2 | 3 | 4 | 5, text: string) {
  switch (level) {
    case 1:
      return <h1>{text}</h1>;
    case 2:
      return <h2>{text}</h2>;
    case 3:
      return <h3>{text}</h3>;
    case 4:
      return <h4>{text}</h4>;
    case 5:
      return <h5>{text}</h5>;
  }
  return null;
}

function renderField(
  field: FeedbackFieldDef,
  values: Record<string, unknown>,
  graph: FeedbackFieldGraph,
  cache: Map<string, boolean>,
  visiting: Set<string>
): ReactNode {
  switch (field.type) {
    case "text":
      return (
        <FeedbackTextField
          key={field.name}
          name={field.name}
          label={field.label}
          helpText={field.helpText}
          required={field.required}
          placeholder={field.placeholder}
        />
      );
    case "textarea":
      return (
        <FeedbackTextField
          key={field.name}
          name={field.name}
          label={field.label}
          helpText={field.helpText}
          required={field.required}
          placeholder={field.placeholder}
          multiline
        />
      );
    case "phone":
      return (
        <FeedbackPhoneField
          key={field.name}
          name={field.name}
          label={field.label}
          helpText={field.helpText}
          required={field.required}
          placeholder={field.placeholder}
        />
      );
    case "email":
      return (
        <FeedbackEmailField
          key={field.name}
          name={field.name}
          label={field.label}
          helpText={field.helpText}
          required={field.required}
          placeholder={field.placeholder}
        />
      );
    case "number":
      return (
        <FeedbackNumberField
          key={field.name}
          name={field.name}
          label={field.label}
          helpText={field.helpText}
          required={field.required}
          placeholder={field.placeholder}
          min={field.min}
          max={field.max}
          step={field.step}
        />
      );
    case "rating":
      return (
        <FeedbackRatingField
          key={field.name}
          name={field.name}
          label={field.label}
          helpText={field.helpText}
          max={field.max}
        />
      );
    case "toggle": {
      const allChildren = field.children ?? [];
      const visibleChildren = allChildren.filter((child) =>
        isFieldVisible(child, values, graph, cache, visiting)
      );
      return (
        <FeedbackToggleField
          key={field.name}
          name={field.name}
          label={field.label}
          helpText={field.helpText}
        >
          {allChildren.length > 0 && (
            <AnimatePresence initial={false}>
              {visibleChildren.map((child) => (
                <FeedbackFieldNode
                  key={child.name}
                  field={child}
                  values={values}
                  graph={graph}
                  cache={cache}
                  visiting={visiting}
                />
              ))}
            </AnimatePresence>
          )}
        </FeedbackToggleField>
      );
    }
    case "select":
      return (
        <FeedbackSelectField
          key={field.name}
          name={field.name}
          label={field.label}
          helpText={field.helpText}
          required={field.required}
          placeholder={field.placeholder}
        >
          {(field.options ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </FeedbackSelectField>
      );
    case "checkbox-group":
      return (
        <FeedbackCheckboxGroup
          key={field.name}
          name={field.name}
          label={field.label}
          helpText={field.helpText}
          options={field.options ?? []}
        />
      );
    case "multi-value":
      return (
        <FeedbackMultiValueField
          key={field.name}
          name={field.name}
          label={field.label}
          helpText={field.helpText}
          placeholder={field.placeholder}
        />
      );
    case "multi-select-tags":
      return (
        <FeedbackMultiSelectTagsField
          key={field.name}
          name={field.name}
          label={field.label}
          helpText={field.helpText}
          options={field.options ?? []}
        />
      );
    case "dropdown-multi-select":
      return (
        <FeedbackDropdownMultiSelectField
          key={field.name}
          name={field.name}
          label={field.label}
          helpText={field.helpText}
          options={field.options ?? []}
          placeholder={field.placeholder}
        />
      );
    case "label":
      return (
        <section className="cf-feedback-structure-block">
          {renderHeading(field.headingLevel ?? 2, field.label)}
          {field.helpText ? <p className="cf-help">{field.helpText}</p> : null}
        </section>
      );
    case "list": {
      const ListTag = field.listStyle === "ol" ? "ol" : "ul";
      return (
        <section className="cf-feedback-structure-block">
          {field.label ? <h3>{field.label}</h3> : null}
          {field.helpText ? <p className="cf-help">{field.helpText}</p> : null}
          <ListTag aria-label={field.label || field.name}>
            {(field.items ?? []).map((item, index) => (
              <li key={`${field.name}-${index}`}>{item}</li>
            ))}
          </ListTag>
        </section>
      );
    }
    case "separator":
      return field.separatorStyle === "space" ? (
        <div
          role="separator"
          aria-orientation="horizontal"
          className="cf-feedback-separator-space"
        />
      ) : (
        <hr className="cf-feedback-separator" />
      );
    default:
      return null;
  }
}
